import { Button, IconButton, type ButtonProps } from "@mui/material";
import { XIcon, type IconProps } from "@phosphor-icons/react";
import { type ElementType, type ReactNode } from "react";
import { useTranslations } from "use-intl";

type SearchResultButtonProps = Pick<ButtonProps, "onClick"> & {
  icon: ElementType<IconProps>;
  primaryText: string;
  secondaryText?: string;
  code?: string;
  onRemove?: () => void;
};

export function SearchResultButton({
  icon: Icon,
  onClick,
  primaryText,
  secondaryText,
  code,
  onRemove,
}: SearchResultButtonProps) {
  const t = useTranslations();

  return (
    <div className="relative flex w-full items-center">
      <Button
        fullWidth
        onClick={onClick}
        variant="text"
        sx={{
          p: "calc(var(--spacing) * 1.5) calc(var(--spacing) * 3) calc(var(--spacing) * 1.5) calc(var(--spacing) * 1.5)",
          paddingInlineEnd: onRemove
            ? "calc(var(--spacing) * 13)"
            : "calc(var(--spacing) * 1.5)",
          borderRadius: 4,
          gap: "calc(var(--spacing) * 3)",
        }}
      >
        <div className="shrink-0 rounded-xl bg-surface-background p-2.5">
          <Icon color="var(--color-text-primary)" size={20} />
        </div>
        <div className="grow overflow-hidden text-start text-sm whitespace-nowrap">
          <div className="overflow-hidden font-medium overflow-ellipsis text-text-primary">
            {primaryText}
          </div>
          {secondaryText ? (
            <div className="overflow-hidden font-normal overflow-ellipsis text-text-tertiary">
              {secondaryText}
            </div>
          ) : null}
        </div>
        {code ? (
          <div className="shrink-0 text-sm font-medium text-text-tertiary uppercase">
            {code}
          </div>
        ) : null}
      </Button>

      {onRemove ? (
        <IconButton
          onClick={onRemove}
          type="button"
          size="small"
          aria-label={t("remove")}
          sx={{
            position: "absolute",
            insetInlineEnd: "calc(var(--spacing) * 1.5)",
          }}
        >
          <XIcon size={20} />
        </IconButton>
      ) : null}
    </div>
  );
}

type Props<T> = {
  heading?: ReactNode;
  data: T[];
  renderItem: (item: T) => ReactNode;
  getItemKey: (item: T) => React.Key;
};

export function SearchResultList<T>({
  heading,
  data,
  renderItem,
  getItemKey,
}: Props<T>) {
  return (
    <>
      {heading ? (
        <div className="w-full pt-3">
          <span className="text-sm font-medium text-text-primary">
            {heading}
          </span>
        </div>
      ) : null}

      <ul
        style={{
          width: "calc(100% + 10px)",
        }}
        className="mt-3 ltr:-translate-x-[5px] rtl:translate-x-[5px]"
      >
        {data.map((item) => (
          <li key={getItemKey(item)}>{renderItem(item)}</li>
        ))}
      </ul>
    </>
  );
}
